<!-- src/js/widgets/manager-requests/components/RequestDetail.vue -->
<template>
  <div class="manager-requests__detail">
    <!-- НЕСКРОЛЯЩИЙСЯ ХЭДЕР -->
    <div class="manager-requests__detail-header">
      <div class="manager-requests__detail-status">
        <label>Статус заявки:</label>
        <Select
          v-model="selectedStatus"
          :options="statusOptions"
          optionLabel="label"
          optionValue="value"
          :disabled="loading"
        />
        <Button
          v-if="hasStatusChanged"
          label="Сохранить"
          icon="pi pi-save"
          @click="saveStatus"
          class="manager-requests__detail-save-btn"
          :disabled="loading"
        />
      </div>
    </div>

    <!-- КОНТЕЙНЕР ТАБОВ С ФИКСИРОВАННОЙ ВЫСОТОЙ -->
    <div class="manager-requests__detail-tabs-container">
      <!-- Правильная структура Tabs -->
      <Tabs v-model:value="activeTab">
        <TabList>
          <Tab value="info">Информация о заявке</Tab>
          <Tab value="files">Прикрепленные документы</Tab>
          <Tab value="history">История статусов</Tab>
        </TabList>

        <TabPanels>
          <!-- Информация о заявке -->
          <TabPanel value="info">
            <div class="manager-requests__detail-info-list">
              <!-- Основная информация + кнопка ответа -->
              <div class="manager-requests__detail-main-info">
                <div class="manager-requests__detail-basic-info">
                  <div class="manager-requests__detail-info-item">
                    <strong>ID заявки:</strong>
                    <span>#{{ request.id }}</span>
                  </div>
                  <div class="manager-requests__detail-info-item">
                    <strong>Дата создания:</strong>
                    <span>{{ formatDate(request.createdAt) }}</span>
                  </div>
                  <div class="manager-requests__detail-info-item">
                    <strong>Компания:</strong>
                    <span>{{ request.companyName || '—' }}</span>
                  </div>
                  <div class="manager-requests__detail-info-item">
                    <strong>E-mail:</strong>
                    <span>{{ request.email || '—' }}</span>
                  </div>
                </div>
                <Button
                  label="Ответить"
                  icon="pi pi-reply"
                  @click="emit('reply')"
                  :disabled="loading"
                  class="manager-requests__detail-reply-btn"
                />
              </div>

              <!-- Поля пользовательской формы -->
              <div
                v-for="(field, index) in request.fields"
                :key="index"
                class="manager-requests__detail-info-item"
              >
                <strong>{{ field.fieldName }}:</strong>
                <p class="manager-requests__detail-text">{{ field.fieldValue || '—' }}</p>
              </div>
            </div>
          </TabPanel>

          <!-- Прикрепленные документы -->
          <TabPanel value="files">
            <!-- Форма загрузки файлов -->
            <div class="manager-requests__detail-files-upload">
              <FileUpload
                ref="fileUploadRef"
                name="replyAttachments[]"
                :multiple="true"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                :maxFileSize="20971520"
                :customUpload="true"
                :auto="false"
                :previewWidth="0"
                chooseLabel="Выбрать файлы"
                @select="onFileSelect"
                @remove="onFileRemove"
                v-model:files="attachedFiles"
                :disabled="loading"
                cancelLabel="Очистить"
                :showUploadButton="false"
                :key="fileUploadKey"
              />
              <!-- Кнопка сохранения файлов -->
              <div v-if="attachedFiles.length > 0" class="manager-requests__detail-files-actions">
                <Button
                  :label="loading ? 'Загрузка...' : 'Сохранить файлы'"
                  :icon="loading ? 'pi pi-spin pi-spinner' : 'pi pi-upload'"
                  @click="saveFiles"
                  :disabled="loading"
                />
              </div>
            </div>

            <!-- Список СОХРАНЕННЫХ файлов -->
            <div
              v-if="request.files && request.files.length"
              class="manager-requests__detail-saved-files"
            >
              <ul>
                <li v-for="(file, index) in request.files" :key="index">
                  <a :href="file.url" target="_blank">{{ file.name }}</a>
                  <span class="manager-requests__detail-file-size">({{ file.size }})</span>
                </li>
              </ul>
            </div>
          </TabPanel>

          <!-- История статусов -->
          <TabPanel value="history">
            <div
              v-if="request.history && request.history.length"
              class="manager-requests__detail-history"
            >
              <div
                v-for="(entry, index) in request.history"
                :key="index"
                class="manager-requests__detail-history-item"
              >
                <span class="manager-requests__detail-history-status">{{ entry.status }}</span>
                <span class="manager-requests__detail-history-date">{{
                  formatDate(entry.date)
                }}</span>
              </div>
            </div>
            <div v-else class="manager-requests__detail-no-history">
              История статусов отсутствует
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import Button from 'primevue/button';
import Select from 'primevue/select';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import FileUpload from 'primevue/fileupload';
import type { Request, RequestStatus } from '../types';

const props = defineProps<{
  request: Request;
  loading: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'status-updated', status: RequestStatus): void;
  (e: 'files-uploaded', files: File[]): void;
  (e: 'reply'): void;
}>();

const activeTab = ref('info');

const statusOptions = [
  { label: 'Новая', value: 'Новая' },
  { label: 'В работе', value: 'В работе' },
  { label: 'Требуется информация', value: 'Требуется информация' },
  { label: 'Исполнена', value: 'Исполнена' },
  { label: 'Отклонена', value: 'Отклонена' },
];

const selectedStatus = ref<RequestStatus>(props.request.status);
const attachedFiles = ref<File[]>([]);
const fileUploadRef = ref();
const fileUploadKey = ref(0);

const hasStatusChanged = computed(() => {
  return selectedStatus.value !== props.request.status;
});

watch(
  () => props.request.status,
  (newStatus) => {
    selectedStatus.value = newStatus;
  },
);

function saveStatus() {
  if (hasStatusChanged.value) {
    emit('status-updated', selectedStatus.value);
  }
}

function onFileSelect(event: any) {
  if (props.loading) return;

  const files = event.files;
  if (files && Array.isArray(files)) {
    const validFiles = files.filter((file: any): file is File => file instanceof File);
    attachedFiles.value.push(...validFiles);
  }
}

function onFileRemove(event: any) {
  if (props.loading) return;

  const removedFile = event.file;
  attachedFiles.value = attachedFiles.value.filter(
    (f) => f.name !== removedFile.name || f.size !== removedFile.size,
  );
}

function saveFiles() {
  if (attachedFiles.value.length > 0 && !props.loading) {
    emit('files-uploaded', [...attachedFiles.value]);
  }
}

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleString('ru-RU');
}

// Экспортируем метод для очистки извне
defineExpose({
  clearAttachedFiles: () => {
    attachedFiles.value = [];
    fileUploadKey.value++;
  },
});
</script>
